/**
 * **P-026** — which workspace the shell is looking at.
 *
 * Measured on all ten parity boards: opening `/boards/<id>` on monday selects **that board's
 * workspace** in the switcher, expands the folder chain down to the board and highlights its
 * row (`PDT ▸ PDT-Product Flow Board Ulti…`, `TO REVIEW ▸ MK-MGMT-Tasks`). Ours resolved the
 * workspace as `params.workspaceId ?? lastWorkspaceId ?? recent[0]`, and `/boards/:boardId`
 * carries no `workspaceId` — so the tree stayed on whatever was last selected and showed the
 * same three rows on every board, with the open board neither revealed nor highlighted.
 *
 * The reveal and the highlight were already built (`Sidebar` S2 + `SidebarTree`'s `selectedId`);
 * the only thing missing was this resolution, so it is the one place it lives.
 *
 * `shell.board` already carries `workspaceId` — the board screen reads the same query, so this
 * is a cache hit rather than a second round trip — and the user's manual switcher choice still
 * wins everywhere a board is not open, which is what "keep the choice within a workspace" means.
 */
import { useBoardSummary } from './bootstrap';
import { isValidId } from './urls';

export interface ShellWorkspaceInput {
  /** `params.workspaceId` — an explicit workspace route always wins. */
  routeWorkspaceId: string | null;
  /** The board in the URL, if any. */
  boardId: string | null;
  /** The workspace the user last looked at (`shell.lastWorkspaceId`). */
  lastWorkspaceId: string | null;
  /**
   * The cold-start landing workspace from `shell.bootstrap` — T-9h: the account's **default**
   * workspace ("Main workspace") as the caller may read it, not whichever workspace sorts first.
   */
  fallbackWorkspaceId: string | null;
}

/**
 * The rule itself, pure: an explicit workspace route wins, then the open board's workspace,
 * then the user's last choice, then the account's default workspace (T-9h). Unchanged by that
 * round — only what the caller passes as `fallbackWorkspaceId` changed.
 */
export function resolveShellWorkspaceId(input: {
  routeWorkspaceId: string | null;
  boardWorkspaceId: string | null;
  lastWorkspaceId: string | null;
  fallbackWorkspaceId: string | null;
}): string | null {
  return (
    input.routeWorkspaceId ?? input.boardWorkspaceId ?? input.lastWorkspaceId ?? input.fallbackWorkspaceId ?? null
  );
}

export function useShellWorkspaceId(input: ShellWorkspaceInput): string | null {
  /*
   * The hook runs on every screen, so it is never conditional; the query is disabled off a
   * board — and off a *valid* board id, because `/boards/not-a-number` renders the 404 panel
   * without asking the server anything (R2) and the shell must not ask either.
   */
  const board = useBoardSummary(isValidId(input.boardId) ? input.boardId : null);
  const boardWorkspaceId = (board.data as { workspaceId?: string | null } | undefined)?.workspaceId ?? null;
  return resolveShellWorkspaceId({
    routeWorkspaceId: input.routeWorkspaceId,
    boardWorkspaceId,
    lastWorkspaceId: input.lastWorkspaceId,
    fallbackWorkspaceId: input.fallbackWorkspaceId,
  });
}
